import { ReactElement, useCallback, useMemo } from 'react'
import { LocalCoordinateSystem } from '@editorUtils'
import { distanceXY } from '@geometry'
import { find } from 'lodash-es'
import { v4 } from 'uuid'
import { Alert, Stack } from '@mui/material'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { DeleteButton, SaveButton } from '@ui/actions'
import { ErrorField, Form } from '@ui/forms'
import { NUM_DECIMALS_ROUND_FLOATS, roundFloatToDecimalPlaces } from 'src/geometry/floatHacks'
import { FormFields } from '../FormFields'
import { WidthAnchorMode } from '../FormFields/utils'
import OpeningFormSceneMarkers from '../OpeningFormSceneMarkers'
import { openingSchema } from './schema'

interface FormState {
  globalStart: {
    x: number
    y: number
    z: number
  }
  globalEnd: {
    x: number
    y: number
    z: number
  }
  wallGuid: string
  openingGuid: string
  xStart: number
  yStart: number
  xEnd: number
  yEnd: number
  openingWidth: number | ''
  openingHeight: number | ''
  openingWidthAnchorMode: WidthAnchorMode
}

interface Props {
  openingGuid: string
  wall: ShapeObject
  handleSubmit: (opening: Opening) => void
  handleDelete: () => void

  isSubmitting?: boolean
  isDeleting?: boolean
  mode?: '2D' | '3D'
}

const GeometryForm = ({
  wall,
  handleSubmit,
  handleDelete,
  openingGuid,
  isSubmitting,
  isDeleting,
  mode,
}: Props): ReactElement | null => {
  const opening = useMemo(
    () => find(wall.openings, { guid: openingGuid }),
    [wall, openingGuid],
  ) as Opening

  const wallCoordinateSystem = useMemo(() => {
    return LocalCoordinateSystem.makeFromPolygon(wall.shape.points)
  }, [wall])

  const defaultValues = useMemo(() => {
    if (!opening) return

    const retransform = wallCoordinateSystem.matrixTransform.clone().invert()

    const [openingP1, openingP2, openingP3] = opening.shape.points

    const projectedOpeningP1 = openingP1.applyMatrix4(retransform)
    const projectedOpeningP3 = openingP3.applyMatrix4(retransform)

    return {
      wallGuid: wall.guid,
      openingGuid: opening.guid,
      xStart: Math.abs(projectedOpeningP1.x),
      yStart: Math.abs(projectedOpeningP1.y),
      xEnd: Math.abs(projectedOpeningP3.x),
      yEnd: Math.abs(projectedOpeningP3.y),
      globalStart: {
        x: openingP1.x,
        y: openingP1.y,
        z: openingP1.z,
      },
      globalEnd: {
        x: openingP3.x,
        y: openingP3.y,
        z: openingP3.z,
      },
      openingWidth: roundFloatToDecimalPlaces(
        distanceXY(openingP1, openingP2),
        NUM_DECIMALS_ROUND_FLOATS,
      ),
      openingHeight: roundFloatToDecimalPlaces(
        openingP3.z - openingP1.z,
        NUM_DECIMALS_ROUND_FLOATS,
      ),
      openingWidthAnchorMode: 'start',
      isIntersecting: false,
    } as FormState
  }, [opening, wallCoordinateSystem.matrixTransform, wall.guid])

  const buildOpeningAndHandleSubmit = useCallback(
    (state: FormState) => {
      const p0 = state.globalStart
      const p2 = state.globalEnd
      const p1 = new ImmutableVector3(p2.x, p2.y, p0.z)
      const p3 = new ImmutableVector3(p0.x, p0.y, p2.z)
      const newOpening = {
        guid: state.openingGuid,
        shape: {
          points: [p0, p1, p2, p3],
          guid: v4(),
        },
      }

      handleSubmit(newOpening as Opening)
    },
    [handleSubmit],
  )

  if (!opening) return null

  return (
    <Form
      key={openingGuid}
      onSubmit={(state: FormState) => buildOpeningAndHandleSubmit(state)}
      validationSchema={openingSchema}
      validationContext={{ wall }}
      defaultValues={defaultValues}
      noValidate
    >
      <Stack direction="column" spacing={1} p={1}>
        <OpeningFormSceneMarkers />
        <FormFields isEditable wall={wall} mode={mode} />
        <ErrorField name="isIntersecting" />
        {!opening.is_local && (
          <Alert severity="warning">
            Einstellungen der Fensterstürze und Rippen (Querschnitte, Nachweiseinstellungen,
            Gruppierung etc.) werden bei einer Änderung der Öffnung zurückgesetzt.
          </Alert>
        )}
        <Stack direction="row" justifyContent="end" spacing={1}>
          <SaveButton type="submit" loading={isSubmitting} fullWidth disabled={isDeleting}>
            Speichern
          </SaveButton>

          <DeleteButton
            onClick={handleDelete}
            size="small"
            fullWidth
            loading={isDeleting}
            disabled={isSubmitting}
          >
            Löschen
          </DeleteButton>
        </Stack>
      </Stack>
    </Form>
  )
}

export default GeometryForm
